import {
  IonPage,
  IonContent,
  useIonToast,
  useIonViewDidEnter,
  useIonViewWillEnter,
  useIonViewWillLeave,
  IonModal,
  IonFooter,
  IonToolbar,
  IonHeader,
  IonTitle,
} from "@ionic/react";
import { UploadInput } from "@greeter-venue/components/UploadInput";
import { sunnyOutline, walletOutline } from "ionicons/icons";
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as uuid from "uuid";

import {
  Container,
  DatePeriodInput,
  FloatingBackButton,
  GradientButton,
  LazyCoverImage,
  PageContainer,
  PageCover,
  ProductCard,
  RoundedButton,
  useInput,
  usePriceInput,
} from "@greeter/matter";

import { useResizeImage } from "@greeter/image";

import coverImage from "../../assets/coverimage.jpg";

import { Product, SpecialPricePeriod } from "@greeter/core";

import { doNothing, doNothingAsync } from "@greeter/util";
import { logger } from "@greeter/log";
import { TextField } from "@greeter/matter";
import { CreateProductRequest, UpdateProductRequest } from "@greeter/venue-api";
import { classNames } from "@greeter-venue/utility";
import { TitledInput } from "@greeter-venue/components/TitledInput";
import HorizontalLine from "@greeter-venue/components/HorizontalLine";
import DangerText from "@greeter-venue/components/DangerText";
import DescriptiveText from "@greeter-venue/components/DescriptiveText";
import { IconInput } from "@greeter-venue/components/IconInput";

import css from "./ProductPage.module.scss";
import { DatePeriod, LenientDatePeriod } from "@greeter/date";
import {
  DeleteButton,
  EditItemToolbar,
} from "@greeter-venue/components/EditItemToolbar";
import { useInitEffect } from "@greeter/hooks";

const log = logger("[ProductPage]");

function tryOr<T>(fn: () => T, _default: T) {
  try {
    return fn();
  } catch {
    return _default;
  }
}

const dateFormatter = new Intl.DateTimeFormat("da", {
  dateStyle: "short",
  timeStyle: "short",
});

const priceFormatter = new Intl.NumberFormat("da-DK");

function SpecialPricePeriodItem(props: {
  specialPricePeriod: SpecialPricePeriod;
  onChange: (specialPricePeriod: SpecialPricePeriod) => void;
}) {
  const priceInput = usePriceInput({
    initialValue: props.specialPricePeriod?.price ?? 0,
  });

  useEffect(() => {
    props.onChange({
      ...props.specialPricePeriod,
      price: priceInput.price,
    });
  }, [priceInput.price]);

  return (
    <div
      style={{
        display: "grid",
        flexDirection: "column",
        gap: "1rem",
        justifyItems: "center",
        justifyContent: "center",
        alignItems: "center",
        paddingBottom: "1rem",
      }}
    >
      <DatePeriodInput
        period={props.specialPricePeriod.period}
        onChange={(dp) => {
          try {
            props.onChange({
              ...props.specialPricePeriod,
              period: new LenientDatePeriod(dp),
              price: priceInput.price,
            });

            console.debug("Change dateperiod to input", priceInput.price);
          } catch {
            console.warn("The input is invalid. Period was not updated.", dp);
          }
        }}
      />
      <IconInput
        style={{ width: "100%" }}
        {...priceInput.bind}
        icon={walletOutline}
      />
    </div>
  );
}

export type OnUpdateArgs = {
  product: UpdateProductRequest;
  specialPricePeriods: Array<SpecialPricePeriod>;
  specialPricePeriodsForDeletion: Array<string>;
  newCover?: File;
};
export type OnCreateArgs = {
  createArgs: CreateProductRequest;
  cover: File;
  specialPricePeriods: Array<SpecialPricePeriod>;
};
export type ProductPageProps = React.PropsWithChildren & {
  product?: Product;
  specialPricePeriods?: Array<SpecialPricePeriod>;
  onDelete?: (productId: string) => void;
  onUpdate?: (args: OnUpdateArgs) => void;
  onCreate?: (args: OnCreateArgs) => void;
};

export function ProductPage({
  product,
  specialPricePeriods,
  onCreate = doNothing,
  onDelete = doNothing,
  onUpdate = doNothing,
}: ProductPageProps) {
  const contentRef = useRef<HTMLIonContentElement>(null);

  const isUpdating = !!product;

  const [_specialPricePeriods, setSpecialPricePeriods] = useState(
    specialPricePeriods
      ?.toSorted((a, b) => a.id.localeCompare(b.id))
      .map((ssp) => ({ ...ssp })) ?? []
  );
  useEffect(() => {
    if (specialPricePeriods) {
      setSpecialPricePeriods(specialPricePeriods.map((ssp) => ({ ...ssp })));
    }
  }, [specialPricePeriods]);

  const [editSpecialPricePeriods, setEditSpecialPricePeriods] = useState(false);

  const [title, setTitle] = useState(product?.title ?? "");
  const [imageFile, setImageFile] = useState<File | undefined>();

  const { resizedFile, resizedBlob, url } = useResizeImage(imageFile, {
    preserveAspectRatio: true,
    width: 1024,
  });

  const [price, bind, setPriceInput] = usePriceInput({
    initialValue: product?.price ?? 0,
  });

  const [showToast] = useIonToast();
  const [specialPricePeriodsForDeletion, setSpecialPricePeriodsForDeletion] =
    useState<Set<string>>(new Set());

  useEffect(() => {
    setSpecialPricePeriods(
      _specialPricePeriods.filter(
        (s) => !specialPricePeriodsForDeletion.has(s.id)
      )
    );
  }, [specialPricePeriodsForDeletion]);

  const {
    bind: descriptionBind,
    value: description,
    setValue: setDescription,
  } = useInput({ initialValue: product?.description });

  useIonViewWillEnter(() => {
    contentRef.current?.scrollToTop();
  }, []);

  useInitEffect(
    () => {
      if (!product) return;

      setTitle(product.title);
      setDescription(product.description);
      setPriceInput(`${product.price ?? 0}`.replace(".", ","));

      setImageFile(undefined);
    },
    [product],
    () => !!product
  );

  useIonViewWillLeave(() => {
    setTitle("");
    setDescription("");
    setPriceInput("");
    setImageFile(undefined);
    setSpecialPricePeriods([]);
    setSpecialPricePeriodsForDeletion(new Set());
  });

  const newProduct = useMemo(() => {
    const p = Product.createDefault();

    p.id = product?.id ?? "";
    p.title = title;
    p.price = price;
    p.coverUrl = url ? url : product?.coverUrl ?? "";
    p.description = description;

    return p;
  }, [description, resizedBlob, price, product?.coverUrl, product?.id, title]);

  return (
    <IonPage>
      <IonContent ref={contentRef}>
        <PageCover>
          <LazyCoverImage src={coverImage} fade />
        </PageCover>
        <PageContainer>
          <FloatingBackButton />
          <div className={css.ProductPage}>
            <h3 style={{ margin: "3rem 1rem" }}>
              <b>
                {product ? "OPDATER" : "OPRET"}
                <span style={{ color: "var(--gm-color-primary-highlight)" }}>
                  <b> BORDPAKKE</b>
                </span>
              </b>
            </h3>
            <TitledInput required title="Pakke titel">
              <IconInput
                {...classNames(
                  product && title !== product.title ? css.Changed : css.New
                )}
                value={title}
                onChange={(e: any) => setTitle(e.target.value)}
                icon={sunnyOutline}
              />
            </TitledInput>
            <TitledInput required title="Billede">
              <UploadInput
                accept="image/*"
                {...classNames(css.New)}
                value={imageFile?.name ?? ""}
                onFileChosen={setImageFile}
              />
            </TitledInput>
            <TitledInput required title="Pris">
              <IconInput
                {...classNames(
                  product && price !== product.price ? css.Changed : css.New
                )}
                {...bind}
                icon={walletOutline}
              />
            </TitledInput>
            <TitledInput required title="Kort beskrivelse">
              <TextField detectChanges {...descriptionBind} />
            </TitledInput>
            <DescriptiveText>
              Bordpakker vises altid med teksten: "inkl. bord"
              <br />
              Du behøver derfor kun nævne pakkens indhold.
            </DescriptiveText>

            <TitledInput
              title="Priser i perioder"
              onClick={() => {
                setEditSpecialPricePeriods(true);
              }}
            >
              <div className={css.Rows}>
                {_specialPricePeriods.map((ppp) => {
                  return (
                    <div>
                      <span>
                        {tryOr(
                          () => dateFormatter.format(ppp.period.from),
                          "Fejl i formatering"
                        )}
                      </span>
                      -
                      <span>
                        {tryOr(
                          () => dateFormatter.format(ppp.period.to),
                          "Fejl i formatering"
                        )}
                      </span>
                      <span>
                        {tryOr(
                          () => priceFormatter.format(ppp.price),
                          "Fejl i formatering"
                        )}{" "}
                        kr.
                      </span>
                    </div>
                  );
                })}
                {(!_specialPricePeriods ||
                  _specialPricePeriods.length === 0) && (
                  <button
                    style={{
                      width: "100%",
                      padding: "0.5rem 0",
                      placeContent: "center",
                      marginTop: "0.5rem",
                      borderRadius: "10px",
                      color: "white",
                      backgroundColor: "transparent",
                      border: "1px dashed white",
                    }}
                    onClick={() => {
                      setEditSpecialPricePeriods(true);
                    }}
                  >
                    Tilret specielle priser
                  </button>
                )}
              </div>
            </TitledInput>

            <HorizontalLine
              beforeSlot={
                <h4 style={{ margin: "0", padding: "0 0.5rem 0 1rem" }}>
                  Forhåndsvisning
                </h4>
              }
            />
            <div className={css.PreviewSection}>
              <div className={css.Preview}>
                <div className={css.PreviewInfo}>
                  <div>Billede</div>
                  <div>Produkt info</div>
                </div>
                <ProductCard
                  product={newProduct ?? product ?? Product.createDefault()}
                />
              </div>
              {!isUpdating ? (
                <>
                  <RoundedButton
                    active
                    onClick={async () => {
                      const args = {
                        title,
                        description,
                        price,
                      } as CreateProductRequest;
                      if (resizedFile) {
                        onCreate({
                          createArgs: args,
                          cover: resizedFile,
                          specialPricePeriods: _specialPricePeriods,
                        });
                      } else {
                        showToast("Husk at uploade et billede", 1000);
                      }
                    }}
                    activeClassName={css.ActionButton}
                  >
                    Opret produkt
                  </RoundedButton>
                  <DescriptiveText>
                    Har du rettelser, kan du altid <br />
                    gå tilbage og redigere produktet
                  </DescriptiveText>
                </>
              ) : (
                <>
                  <RoundedButton
                    active
                    onClick={async () => {
                      const args = {
                        id: product?.id,
                        title,
                        description,
                        price,
                      } as UpdateProductRequest;

                      onUpdate({
                        product: args,
                        newCover: imageFile ? resizedFile : undefined,
                        specialPricePeriods: _specialPricePeriods.map(
                          (spp) => ({
                            ...spp,
                            period: new LenientDatePeriod(
                              spp.period
                            ).toStrict(),
                          })
                        ),
                        specialPricePeriodsForDeletion: Array.from(
                          specialPricePeriodsForDeletion
                        ),
                      });
                    }}
                    activeClassName={css.ActionButton}
                  >
                    Opdater produkt
                  </RoundedButton>
                  <div onClick={() => onDelete(product?.id ?? "")}>
                    <DangerText>Slet dette produkt</DangerText>
                  </div>
                </>
              )}
            </div>
          </div>
        </PageContainer>
      </IonContent>
      <IonModal
        isOpen={editSpecialPricePeriods}
        onWillDismiss={() => setEditSpecialPricePeriods(false)}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>Specielle pris perioder</IonTitle>
          </IonToolbar>
        </IonHeader>
        <FloatingBackButton onClick={() => setEditSpecialPricePeriods(false)} />

        <IonContent>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1.5rem",
              paddingBottom: "50vh",
            }}
          >
            <div />
            {_specialPricePeriods.map((spp) => {
              const exists = specialPricePeriods?.find(
                (_spp) => _spp.id === spp.id
              );
              return (
                <div
                  key={spp.id}
                  {...classNames("change-border", !exists && "new")}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "1rem",
                    backgroundColor: "var(--gm-color-default-bg)",
                  }}
                >
                  <EditItemToolbar
                    slots={{
                      end: (
                        <DeleteButton
                          onClick={() =>
                            setSpecialPricePeriodsForDeletion(
                              new Set(
                                specialPricePeriodsForDeletion.add(spp.id)
                              )
                            )
                          }
                        />
                      ),
                    }}
                  />
                  <SpecialPricePeriodItem
                    specialPricePeriod={spp}
                    onChange={(newSpp) => {
                      console.debug(
                        "[SpecialPricePeriodItem][onChange][prev]",
                        spp
                      );
                      console.debug(
                        "[SpecialPricePeriodItem][onChange][new]",
                        newSpp
                      );
                      setSpecialPricePeriods(
                        _specialPricePeriods.map((_spp) =>
                          newSpp.id === _spp.id ? newSpp : _spp
                        )
                      );
                    }}
                  />
                </div>
              );
            })}

            <button
              style={{
                width: "100%",
                padding: "0.5rem 0",
                placeContent: "center",
                marginTop: "0.5rem",
                borderRadius: "10px",
                color: "white",
                backgroundColor: "transparent",
                border: "1px dashed white",
              }}
              onClick={() => {
                setSpecialPricePeriods([
                  ..._specialPricePeriods,
                  {
                    id: uuid.v7(),
                    period: new DatePeriod({
                      from: new Date(),
                      to: new Date(),
                    }),
                    price: 0,
                    productId: product?.id ?? "",
                  },
                ]);
              }}
            >
              + Tilføj ny speciel prisperiode
            </button>
          </div>
        </IonContent>
        <IonFooter>
          <GradientButton
            style={{
              borderRadius: "0",
              width: "100%",
              height: "100%",
            }}
            onClick={async function onDone() {
              setEditSpecialPricePeriods(false);
            }}
          >
            Gem
          </GradientButton>
        </IonFooter>
      </IonModal>
    </IonPage>
  );
}
